import * as React from "react";
import { useStore } from "_core/store";
import { Navigate, useNavigate } from "react-router";
import {
  HypervoltChargerCustomisation as HypervoltChargerCustomisationValidator,
  FormValues,
} from "_core/validators/ChargerCustomisation/Hypervolt";
import { ProductUrls } from "_core/types";
import Mixpanel from "_core/analytics/Mixpanel";
import {
  AnalyticsEvents,
  DataLayerEvent,
} from "_core/analytics/AnalyticsEvents";
import { DataLayer } from "_core/analytics/DataLayer";
import { FormApi, Formy } from "_core/form";
import {
  Button,
  Heading4,
  Margin,
  P,
  RadioCard,
  RadioField,
  Stack,
  Strong,
} from "@ovotech/element";
import { PhoneContact } from "_core/components/PhoneContact";
import { EmailContact } from "_core/components/EmailContact";
import {
  HYPERVOLT_CABLES,
  HYPERVOLT_COLOURS,
} from "_pages/ChargerCustomisation/Hypervolt/constants";
import { CableLengthIcon } from "_core/components/CableLengthIcon";
import { getPriceString } from "_core/utils/format";
import { HypervoltFAQ } from "_pages/ChargerCustomisation/Hypervolt/HypervoltFAQ";
import spaceGrey from "_assets/hypervolt/hypervolt-space-grey.png";
import ultraWhite from "_assets/hypervolt/hypervolt-ultra-white.png";
import ultraBlack from "_assets/hypervolt/hypervolt-ultra-black.png";
import { SplitHeading } from "_core/components";

const baseColourImages = { spaceGrey, ultraWhite, ultraBlack };

export const HypervoltChargerCustomisation = () => {
  const {
    cableLength,
    baseColour,
    setCableLength,
    setBaseColour,
    offStreetParking,
    charger,
  } = useStore();
  const navigate = useNavigate();
  const handleSubmit = async (values: FormValues) => {
    if (!values.cableLength || !values.baseColour) {
      return;
    }
    setCableLength(values.cableLength);
    setBaseColour(values.baseColour);
    navigate(ProductUrls.PersonalDetails);

    Mixpanel.track(AnalyticsEvents.CLICKEDBUTTON, {
      buttonId: "chargerCustomisation-submit-button",
      pageName: ProductUrls.ChargerCustomisation,
      cableLength: values.cableLength,
      baseColour: values.baseColour,
      charger,
    });

    DataLayer.push({
      event: DataLayerEvent.SubmitChargerCustomisation,
      pageName: ProductUrls.ChargerCustomisation,
      cableLength: values.cableLength,
      baseColour: values.baseColour,
      charger,
    });
  };

  if (!offStreetParking) return <Navigate to={ProductUrls.Postcode} />;

  return (
    <>
      <Formy
        initialValues={{
          cableLength,
          baseColour,
        }}
        validationSchema={HypervoltChargerCustomisationValidator}
        onSubmit={handleSubmit}
      >
        {({
          handleSubmit,
          values,
          errors,
          touched,
          setFieldValue,
        }: FormApi<FormValues>) => {
          return (
            <form
              data-testid="chargerCustomisation-form"
              onSubmit={handleSubmit}
            >
              <Stack spaceBetween={10}>
                <Stack spaceBetween={8}>
                  <SplitHeading part1="Customise" part2="your charger" />
                  <div>
                    <P size={"lg"}>
                      Personalise your <Strong>Hypervolt 3 Pro</Strong> by
                      choosing the charger colour and cable length.
                    </P>
                    <P size={"sm"} className={"SurfaceVariant"}>
                      Need help choosing? Call us on <PhoneContact />, or email
                      us at <EmailContact />.
                    </P>
                  </div>
                </Stack>
                <div>
                  <RadioField
                    className={"RadioField"}
                    id="cableLength"
                    name="cableLength"
                    label={
                      <Heading4 className={"SurfaceHeading"}>
                        Choose a cable length
                      </Heading4>
                    }
                    hint={
                      <P>
                        Shorter cables are great if you prefer a lighter option.
                        Choose a longer cable if your EV charging port is far
                        away from the charger. For example, if you have more
                        than one EV parked in the driveway. All our cables have
                        a Type 2 plug.
                      </P>
                    }
                    error={touched.cableLength && errors.cableLength}
                  >
                    {HYPERVOLT_CABLES.map((cable) => (
                      <RadioCard
                        className={"RadioCard"}
                        key={cable.value}
                        name="cableLength"
                        value={cable.value}
                        id={cable.value}
                        label={
                          <div>
                            <CableLengthIcon cableLength={cable.value} />
                            <Margin top={2} />
                            <P>
                              Charger with <Strong>{cable.label}</Strong> from{" "}
                              <Strong>{getPriceString(cable.price)}¹</Strong>
                            </P>
                          </div>
                        }
                        checked={values.cableLength === cable.value}
                        onClick={() => {
                          setFieldValue("cableLength", cable.value);
                          Mixpanel.track(AnalyticsEvents.CLICKEDBUTTON, {
                            buttonId: `cableLength-${cable.value}`,
                            pageName: ProductUrls.ChargerCustomisation,
                            charger,
                            value: cable.value,
                          });
                        }}
                      />
                    ))}
                  </RadioField>

                  <P size={"sm"} className={"SurfaceVariant"}>
                    ¹ Including standard installation. The final cost will be in
                    the quote we send after we’ve checked your installation
                    survey. All prices above include VAT and are applicable to
                    all colour variants.
                  </P>
                </div>
                <RadioField
                  className={"RadioField"}
                  id="baseColour"
                  name="baseColour"
                  label={
                    <Heading4 className={"SurfaceHeading"}>
                      Choose a colour
                    </Heading4>
                  }
                  error={touched.baseColour && errors.baseColour}
                >
                  {HYPERVOLT_COLOURS.map((chargerColor) => (
                    <RadioCard
                      className={"RadioCard"}
                      key={chargerColor.value}
                      name={"baseColour"}
                      value={chargerColor.value}
                      id={chargerColor.value}
                      label={
                        <>
                          <img
                            className={"ChargerImg"}
                            alt={`image of a ${chargerColor.label} charger`}
                            src={baseColourImages[chargerColor.value]}
                          ></img>
                          <Margin top={2} />
                          <P size={"md"}>
                            <Strong>{chargerColor.label}</Strong>
                          </P>
                        </>
                      }
                      checked={values.baseColour === chargerColor.value}
                      onClick={() => {
                        setFieldValue("baseColour", chargerColor.value);
                        Mixpanel.track(AnalyticsEvents.CLICKEDBUTTON, {
                          buttonId: `baseColour-${chargerColor.value}`,
                          pageName: ProductUrls.ChargerCustomisation,
                          charger,
                          value: chargerColor.value,
                        });
                      }}
                    />
                  ))}
                </RadioField>
                <div className={"ButtonContainer"}>
                  <Button
                    variant="outline"
                    iconLeft="chevron-left"
                    data-testid="chargerCustomisation-back-button"
                    onClick={() => navigate(ProductUrls.OffStreetParking)}
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    iconRight="chevron-right"
                    data-testid="chargerCustomisation-submit-button"
                  >
                    Next
                  </Button>
                </div>
              </Stack>
            </form>
          );
        }}
      </Formy>
      <HypervoltFAQ />
    </>
  );
};
